import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Animated,
  Dimensions,
  ScrollView,
  Platform,
  Image,
} from 'react-native';
import {
  Modal,
  Portal,
  Surface,
  Button,
  ProgressBar,
  Chip,
  List,
  Divider,
  IconButton,
  Card,
  Checkbox,
  ActivityIndicator,
  useTheme,
} from 'react-native-paper';
import { useSync } from '../hooks/useSync';
import { useOfflineSync } from '../hooks/useOfflineSync';
import { useSelector } from 'react-redux';
import { RootState } from '../store';

interface SyncIndicatorProps {
  visible: boolean;
  onDismiss: () => void;
  compact?: boolean;
}

interface SyncHistoryItem {
  id: string;
  timestamp: number;
  type: 'success' | 'error' | 'warning';
  message: string;
  details?: string;
}

export const SyncIndicator: React.FC<SyncIndicatorProps> = ({
  visible,
  onDismiss,
  compact = false,
}) => {
  const theme = useTheme();
  const {
    isOnline: legacyIsOnline,
    pendingActionsCount,
    syncInProgress: legacySyncInProgress,
    lastSyncTime,
    lastSyncStatus,
    forceSync: legacyForceSync,
    getSyncStatusText,
  } = useSync();

  // Get comprehensive sync stats (includes SQLite pending responses)
  const offlineSync = useOfflineSync();

  // Use enhanced stats on mobile, legacy on web
  const isOnline = Platform.OS !== 'web' ? offlineSync.isOnline : legacyIsOnline;
  const syncInProgress = Platform.OS !== 'web' ? offlineSync.syncInProgress : legacySyncInProgress;
  const forceSync = Platform.OS !== 'web' ? offlineSync.forceSync : legacyForceSync;
  const totalPendingCount = Platform.OS !== 'web' ? offlineSync.totalPending : pendingActionsCount;
  const waitingToSyncCount = Platform.OS !== 'web' ? offlineSync.waitingToSyncCount ?? totalPendingCount : pendingActionsCount;
  const retryNeededCount = Platform.OS !== 'web' ? offlineSync.retryNeededCount ?? offlineSync.failedResponses ?? 0 : 0;
  const hasFailedItems = Platform.OS !== 'web' && retryNeededCount > 0;
  const recoveryAvailable = Platform.OS !== 'web' && offlineSync.recoveryAvailable;

  const offlineState = useSelector((state: RootState) => state.offline);
  const [expanded, setExpanded] = useState(false);
  const [syncHistory, setSyncHistory] = useState<SyncHistoryItem[]>([]);
  const [selectedRecoveryIds, setSelectedRecoveryIds] = useState<string[]>([]);
  const [detailRecoveryId, setDetailRecoveryId] = useState<string | null>(null);
  const [recoveryActionLoading, setRecoveryActionLoading] = useState(false);
  const [recoveryActionMessage, setRecoveryActionMessage] = useState<string | null>(null);
  const [manualSyncMessage, setManualSyncMessage] = useState<string | null>(null);
  const spinValue = new Animated.Value(0);
  const recoveryWorkItems = Platform.OS !== 'web' ? offlineSync.recoveryWorkItems || [] : [];
  const actionableRecoveryItems = recoveryWorkItems.filter(item => !item.blockedReason);
  const detailRecoveryItem = detailRecoveryId
    ? recoveryWorkItems.find(item => item.id === detailRecoveryId) || null
    : null;

  // Animation for sync icon
  useEffect(() => {
    let animationLoop: Animated.CompositeAnimation | null = null;

    if (syncInProgress) {
      animationLoop = Animated.loop(
        Animated.timing(spinValue, {
          toValue: 1,
          duration: 2000,
          useNativeDriver: true,
        })
      );
      animationLoop.start();
    } else {
      spinValue.setValue(0);
    }

    return () => {
      if (animationLoop) {
        animationLoop.stop();
      }
    };
  }, [syncInProgress]);

  // Update sync history
  useEffect(() => {
    if (lastSyncTime && lastSyncStatus) {
      const historyItem: SyncHistoryItem = {
        id: `${Date.now()}-${Math.random()}`,
        timestamp: lastSyncTime,
        type: lastSyncStatus === 'success' ? 'success' : lastSyncStatus === 'error' ? 'error' : 'warning',
        message: getSyncStatusText(),
        details: lastSyncStatus === 'error' ? 'Sync failed. Please try again.' : undefined,
      };

      setSyncHistory(prev => [historyItem, ...prev.slice(0, 9)]); // Keep last 10 items
    }
  }, [lastSyncTime, lastSyncStatus]);

  useEffect(() => {
    if (!visible || Platform.OS === 'web') {
      return;
    }

    void (async () => {
      await offlineSync.refreshStats?.();
      await offlineSync.refreshRecoveryWorkItems?.();
    })();
  }, [visible]);

  useEffect(() => {
    if (recoveryWorkItems.length === 0) {
      setSelectedRecoveryIds([]);
      return;
    }

	    setSelectedRecoveryIds(previous => {
	      const availableIds = new Set(recoveryWorkItems.filter(item => !item.blockedReason).map(item => item.id));
	      return previous.filter(id => availableIds.has(id));
	    });
	    if (detailRecoveryId && !recoveryWorkItems.some(item => item.id === detailRecoveryId)) {
	      setDetailRecoveryId(null);
	    }
	  }, [recoveryWorkItems.map(item => item.id).join('|')]);

  const getStatusIcon = () => {
    if (!isOnline) return 'wifi-off';
    if (syncInProgress) return 'sync';
    if (hasFailedItems) return 'alert-circle';
    if (waitingToSyncCount > 0) return 'clock-outline';
    if (recoveryAvailable) return 'lifebuoy';
    if (offlineState.syncProgress.errors.length > 0) return 'alert-triangle';
    return 'check-circle';
  };

  const getStatusColor = () => {
    if (!isOnline) return theme.colors.error;
    if (hasFailedItems) return theme.colors.error;
    if (syncInProgress) return theme.colors.primary;
    if (waitingToSyncCount > 0) return theme.colors.onSurfaceVariant;
    if (recoveryAvailable) return theme.colors.error;
    if (offlineState.syncProgress.errors.length > 0) return theme.colors.error;
    return theme.colors.primary;
  };

  const getProgressPercentage = () => {
    const { totalOperations, completedOperations } = offlineState.syncProgress;
    if (totalOperations === 0) return 0;
    return completedOperations / totalOperations;
  };

  const formatTimestamp = (timestamp: number) => {
    const now = new Date();
    const date = new Date(timestamp);
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  const handleManualSync = async () => {
    try {
      setManualSyncMessage(null);
      const result = await forceSync();
      if (result) {
        const hasPhotoRecovery = result.photosUploaded > 0 ||
          result.photosStillFailed > 0 || result.photosMissing > 0;
        setManualSyncMessage(hasPhotoRecovery
          ? `${result.photosUploaded} photo${result.photosUploaded !== 1 ? 's' : ''} uploaded` +
            (result.photosStillFailed > 0
              ? `, ${result.photosStillFailed} still need retry`
              : '') +
            (result.photosMissing > 0
              ? `, ${result.photosMissing} missing on this device`
              : '')
          : 'Sync completed; no recoverable photo uploads were found.');
      }
    } catch (error) {
      console.warn('[SyncIndicator] Manual sync failed:', error);
      setManualSyncMessage('Sync failed. The local queue was preserved; try again.');
    }
  };

  const recoveryTestId = (id: string) => id.replace(/[^a-zA-Z0-9_-]/g, '-');
  const recoveryItemTestId = (item: typeof recoveryWorkItems[number]) => {
    if (item.kind === 'response' && item.itemId != null) {
      return recoveryTestId(`response-${item.reviewId}-${item.itemId}`);
    }
    if (item.kind === 'review') {
      return recoveryTestId(`review-${item.reviewId}`);
    }
    if (item.kind === 'photo' && item.entityId != null) {
      return recoveryTestId(`photo-${item.entityId}`);
    }
    return recoveryTestId(item.id);
  };

	  const toggleRecoverySelection = (id: string) => {
	    if (recoveryWorkItems.find(item => item.id === id)?.blockedReason) {
	      return;
	    }
	    setSelectedRecoveryIds(previous =>
	      previous.includes(id)
	        ? previous.filter(existingId => existingId !== id)
	        : [...previous, id]
	    );
	  };

	  const allRecoveryItemsSelected =
	    actionableRecoveryItems.length > 0 &&
	    actionableRecoveryItems.every(item => selectedRecoveryIds.includes(item.id));

	  const toggleSelectAllRecoveryItems = () => {
	    setSelectedRecoveryIds(
	      allRecoveryItemsSelected
	        ? []
	        : actionableRecoveryItems.map(item => item.id)
	    );
	  };

  const handleRecoverSelected = async () => {
    if (selectedRecoveryIds.length === 0) {
      setRecoveryActionMessage('Select at least one item to keep these changes.');
      return;
    }

    setRecoveryActionLoading(true);
    setRecoveryActionMessage(null);
    try {
      const result = await offlineSync.recoverSelectedWorkItems(selectedRecoveryIds);
      setSelectedRecoveryIds([]);
      setRecoveryActionMessage(
        `Kept these changes for ${result.recovered} item${result.recovered !== 1 ? 's' : ''}` +
        (result.photosUploaded > 0
          ? `; ${result.photosUploaded} photo${result.photosUploaded !== 1 ? 's' : ''} uploaded`
          : '') +
        (result.photosStillFailed > 0
          ? `; ${result.photosStillFailed} photo${result.photosStillFailed !== 1 ? 's' : ''} still need${result.photosStillFailed === 1 ? 's' : ''} retry`
          : '') +
        (result.photosMissing > 0
          ? `; ${result.photosMissing} photo file${result.photosMissing !== 1 ? 's are' : ' is'} missing on this device`
          : '') +
        (result.skipped || result.failed ? ` (${result.skipped} skipped, ${result.failed} failed)` : '')
      );
    } catch (error) {
      console.warn('[SyncIndicator] Selected recovery failed:', error);
      setRecoveryActionMessage('Could not keep these changes for selected items. Try again.');
    } finally {
      setRecoveryActionLoading(false);
    }
  };

  const handleKeepServerSelected = async () => {
    if (selectedRecoveryIds.length === 0) {
      setRecoveryActionMessage('Select at least one item to keep the online version.');
      return;
    }

    setRecoveryActionLoading(true);
    setRecoveryActionMessage(null);
    try {
      const result = await offlineSync.keepServerForSelectedWorkItems(selectedRecoveryIds);
      setSelectedRecoveryIds([]);
      setRecoveryActionMessage(
        `Kept the online version for ${result.keptServer} item${result.keptServer !== 1 ? 's' : ''}` +
        (result.skipped || result.failed ? ` (${result.skipped} skipped, ${result.failed} failed)` : '')
      );
    } catch (error) {
      console.warn('[SyncIndicator] Keep-server recovery choice failed:', error);
      setRecoveryActionMessage('Could not keep the online version for selected items. Try again.');
    } finally {
      setRecoveryActionLoading(false);
    }
  };

  const getPendingSummary = () => {
    const parts = [];
	    if (offlineSync.pendingResponses > 0) {
	      parts.push(`${offlineSync.pendingResponses} response${offlineSync.pendingResponses !== 1 ? 's' : ''}`);
	    }
	    if (offlineSync.pendingPhotos > 0) {
	      parts.push(`${offlineSync.pendingPhotos} photo${offlineSync.pendingPhotos !== 1 ? 's' : ''}`);
	    }
	    if (offlineSync.pendingOnDemandItems > 0) {
      parts.push(`${offlineSync.pendingOnDemandItems} custom item${offlineSync.pendingOnDemandItems !== 1 ? 's' : ''}`);
    }
    if (offlineSync.pendingReviews > 0) {
      parts.push(`${offlineSync.pendingReviews} review${offlineSync.pendingReviews !== 1 ? 's' : ''}`);
    }
    if (pendingActionsCount > 0) {
      parts.push(`${pendingActionsCount} action${pendingActionsCount !== 1 ? 's' : ''}`);
    }
    return parts.length > 0
      ? parts.join(', ')
      : `${totalPendingCount} item${totalPendingCount !== 1 ? 's' : ''}`;
  };

  const getChoiceSummary = () =>
    `${offlineSync.recoverableItems} saved item${offlineSync.recoverableItems !== 1 ? 's' : ''} need a choice`;

  const getPendingDetailText = () => {
    if (totalPendingCount === 0) {
      return null;
    }
    if (!isOnline) {
      return 'Will sync when back online';
    }
    if (hasFailedItems) {
      if (waitingToSyncCount > 0) {
        return `${waitingToSyncCount} waiting, ${retryNeededCount} need retry`;
      }
      return `${retryNeededCount} item${retryNeededCount !== 1 ? 's' : ''} need retry`;
    }
    return 'Waiting for next sync';
  };

  const getEnhancedStatusText = () => {
    if (Platform.OS === 'web') {
      return getSyncStatusText();
    }

    if (syncInProgress) {
      return 'Syncing...';
    }

    if (!isOnline) {
      return totalPendingCount > 0
        ? `Offline - ${totalPendingCount} item${totalPendingCount !== 1 ? 's' : ''} waiting to sync`
        : 'Offline';
    }

    if (hasFailedItems) {
      if (recoveryAvailable) {
        return waitingToSyncCount > 0
          ? `${waitingToSyncCount} waiting, ${retryNeededCount} need retry, ${getChoiceSummary()}`
          : `${retryNeededCount} need retry, ${getChoiceSummary()}`;
      }
      return `${retryNeededCount} item${retryNeededCount !== 1 ? 's' : ''} need retry`;
    }

    if (waitingToSyncCount > 0) {
      if (recoveryAvailable) {
        return `${getPendingSummary()} waiting, ${getChoiceSummary()}`;
      }
      return `Waiting to sync: ${getPendingSummary()}`;
    }

    if (recoveryAvailable) {
      return `Saved changes need a choice - ${offlineSync.recoverableItems} item${offlineSync.recoverableItems !== 1 ? 's' : ''}`;
    }

    return 'All synced';
  };

  const getQueueTitle = () => {
    if (retryNeededCount > 0) {
      return `Sync Queue (${waitingToSyncCount} waiting, ${retryNeededCount} need retry)`;
    }

    return `Waiting to Sync (${waitingToSyncCount} item${waitingToSyncCount !== 1 ? 's' : ''})`;
  };

  const renderCompactView = () => (
    <Surface style={[styles.compactContainer, { backgroundColor: theme.colors.surfaceVariant }]}>
      <View style={styles.compactContent}>
        <Animated.View
          style={[
            styles.iconContainer,
            {
              transform: [{
                rotate: spinValue.interpolate({
                  inputRange: [0, 1],
                  outputRange: ['0deg', '360deg'],
                })
              }]
            }
          ]}
        >
          <IconButton
            icon={getStatusIcon()}
            iconColor={getStatusColor()}
            size={20}
            style={styles.statusIcon}
          />
        </Animated.View>

        <View style={styles.compactText}>
          <Text style={[styles.statusText, { color: theme.colors.onSurface }]} numberOfLines={1}>
            {getEnhancedStatusText()}
          </Text>
          {totalPendingCount > 0 && (
            <Text style={[styles.pendingText, { color: theme.colors.onSurfaceVariant }]}>
              {getPendingDetailText()}
            </Text>
          )}
          {Platform.OS !== 'web' && hasFailedItems && (
            <Text style={[styles.warningText, { color: theme.colors.error }]}>
              {retryNeededCount} item{retryNeededCount !== 1 ? 's' : ''} need retry
            </Text>
          )}
	        </View>

        {totalPendingCount > 0 && isOnline && (
          <Button
            mode="contained"
            compact
            loading={syncInProgress}
            onPress={handleManualSync}
            style={styles.compactSyncButton}
            labelStyle={styles.compactSyncButtonText}
          >
            Sync
          </Button>
        )}
      </View>

      {syncInProgress && (
        <ProgressBar
          progress={getProgressPercentage()}
          color={theme.colors.primary}
          style={styles.progressBar}
        />
      )}
    </Surface>
  );

  const renderQueueVisualization = () => (
    <Card style={styles.queueCard}>
      <Card.Content>
        <Text style={[styles.sectionTitle, { color: theme.colors.onSurface }]}>
          {getQueueTitle()}
        </Text>

        {/* Show breakdown for mobile */}
        {Platform.OS !== 'web' && (totalPendingCount > 0 || recoveryAvailable) && (
          <View style={styles.statsBreakdown}>
	            {offlineSync.pendingResponses > 0 && (
              <Text style={[styles.statItem, { color: theme.colors.onSurfaceVariant }]}>
                • {offlineSync.pendingResponses} response{offlineSync.pendingResponses !== 1 ? 's' : ''} waiting to sync
              </Text>
	            )}
	            {offlineSync.pendingPhotos > 0 && (
	              <Text style={[styles.statItem, { color: theme.colors.onSurfaceVariant }]}>
	                • {offlineSync.pendingPhotos} photo{offlineSync.pendingPhotos !== 1 ? 's' : ''} waiting to sync
	              </Text>
	            )}
            {offlineSync.pendingOnDemandItems > 0 && (
              <Text style={[styles.statItem, { color: theme.colors.onSurfaceVariant }]}>
                • {offlineSync.pendingOnDemandItems} custom item{offlineSync.pendingOnDemandItems !== 1 ? 's' : ''} waiting to sync
              </Text>
            )}
	            {retryNeededCount > 0 && (
	              <Text style={[styles.statItem, { color: theme.colors.error }]}>
	                • {retryNeededCount} item{retryNeededCount !== 1 ? 's' : ''} need retry
	              </Text>
	            )}
            {offlineSync.availableLocalPhotos > 0 && (
              <Text style={[styles.statItem, { color: theme.colors.onSurfaceVariant }]}>
                • {offlineSync.availableLocalPhotos} local photo file{offlineSync.availableLocalPhotos !== 1 ? 's' : ''} available for recovery
              </Text>
            )}
            {offlineSync.missingLocalPhotos > 0 && (
              <Text style={[styles.statItem, { color: theme.colors.error }]}>
                • {offlineSync.missingLocalPhotos} local photo file{offlineSync.missingLocalPhotos !== 1 ? 's are' : ' is'} missing on this device
              </Text>
            )}
            {offlineSync.pendingReviews > 0 && (
              <Text style={[styles.statItem, { color: theme.colors.onSurfaceVariant }]}>
                • {offlineSync.pendingReviews} review{offlineSync.pendingReviews !== 1 ? 's' : ''} waiting to sync
              </Text>
            )}
            {offlineSync.recoveryAvailable && (
              <Text
                testID="sync-indicator-recovery-count"
                style={[styles.statItem, { color: theme.colors.error }]}
              >
                • {offlineSync.recoverableItems} saved item{offlineSync.recoverableItems !== 1 ? 's' : ''} need a choice
              </Text>
            )}
            {pendingActionsCount > 0 && (
              <Text style={[styles.statItem, { color: theme.colors.onSurfaceVariant }]}>
                • {pendingActionsCount} queued action{pendingActionsCount !== 1 ? 's' : ''} waiting to sync
              </Text>
            )}
          </View>
        )}

        {waitingToSyncCount === 0 && retryNeededCount === 0 ? (
          <Text
            testID="sync-indicator-queue-fallback-text"
            style={[styles.emptyQueueText, { color: theme.colors.onSurfaceVariant }]}
          >
            No items in queue
          </Text>
        ) : pendingActionsCount > 0 ? (
          <ScrollView style={styles.queueList}>
            {offlineState.pendingActions.slice(0, 5).map((action, index) => (
              <View key={action.id} style={styles.queueItem}>
                <View style={styles.queueItemContent}>
                  <Chip
                    icon={action.retryCount > 0 ? 'refresh' : 'clock-outline'}
                    style={[
                      styles.actionTypeChip,
                      { backgroundColor: action.retryCount > 0 ? theme.colors.errorContainer : theme.colors.primaryContainer }
                    ]}
                    textStyle={{ fontSize: 12 }}
                  >
                    {action.type.replace('_', ' ').toUpperCase()}
                  </Chip>
                  <Text style={[styles.queueItemText, { color: theme.colors.onSurface }]}>
                    {formatTimestamp(action.timestamp)}
                  </Text>
                  {action.retryCount > 0 && (
                    <Text style={[styles.retryCount, { color: theme.colors.error }]}>
                      Retry {action.retryCount}
                    </Text>
                  )}
                </View>
              </View>
            ))}
            {pendingActionsCount > 5 && (
              <Text style={[styles.moreItemsText, { color: theme.colors.onSurfaceVariant }]}>
                +{pendingActionsCount - 5} more items
              </Text>
            )}
          </ScrollView>
        ) : (
          <Text
            testID="sync-indicator-queue-fallback-text"
            style={[styles.emptyQueueText, { color: theme.colors.onSurfaceVariant }]}
	          >
	            {Platform.OS !== 'web' && (waitingToSyncCount > 0 || retryNeededCount > 0)
	              ? 'Changes are saved and waiting for sync'
	              : 'No queued actions'}
          </Text>
        )}
      </Card.Content>
    </Card>
  );

	  const renderSyncHistory = () => (
    <Card style={styles.historyCard}>
      <Card.Content>
        <Text style={[styles.sectionTitle, { color: theme.colors.onSurface }]}>
          Recent Sync History
        </Text>

        {syncHistory.length === 0 ? (
          <Text style={[styles.emptyHistoryText, { color: theme.colors.onSurfaceVariant }]}>
            No sync history available
          </Text>
        ) : (
          <ScrollView style={styles.historyList}>
            {syncHistory.map((item, index) => (
              <React.Fragment key={item.id}>
                <View style={styles.historyItem}>
                  <IconButton
                    icon={
                      item.type === 'success' ? 'check-circle' :
                      item.type === 'error' ? 'alert-circle' :
                      'alert-triangle'
                    }
                    iconColor={
                      item.type === 'success' ? theme.colors.primary :
                      item.type === 'error' ? theme.colors.error :
                      theme.colors.onSurfaceVariant
                    }
                    size={20}
                  />
                  <View style={styles.historyItemContent}>
                    <Text style={[styles.historyItemText, { color: theme.colors.onSurface }]}>
                      {item.message}
                    </Text>
                    <Text style={[styles.historyTimestamp, { color: theme.colors.onSurfaceVariant }]}>
                      {formatTimestamp(item.timestamp)}
                    </Text>
                    {item.details && (
                      <Text style={[styles.historyDetails, { color: theme.colors.onSurfaceVariant }]}>
                        {item.details}
                      </Text>
                    )}
                  </View>
                </View>
                {index < syncHistory.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </ScrollView>
        )}
      </Card.Content>
    </Card>
	  );

	  const formatRecoveryTitle = (item: typeof recoveryWorkItems[number]) => {
	    const address = item.reviewAddress || item.reviewName;
	    const itemName = item.itemName || item.title;
	    return address ? `${address} • ${itemName}` : itemName;
	  };

	  const renderRecoveryItemDetail = () => {
	    if (!detailRecoveryItem) {
	      return null;
	    }

	    return (
	      <View testID="sync-indicator-recovery-detail-view">
	        <Button
	          mode="text"
	          icon="arrow-left"
	          onPress={() => setDetailRecoveryId(null)}
	          style={styles.recoveryBackButton}
	          testID="sync-indicator-recovery-detail-back-button"
	        >
	          Back
	        </Button>
        <Text
          testID="sync-indicator-recovery-detail-title"
          style={[styles.recoveryItemTitle, styles.recoveryDetailTitle, { color: theme.colors.onSurface }]}
        >
          {formatRecoveryTitle(detailRecoveryItem)}
        </Text>
	        {detailRecoveryItem.blockedReason && (
	          <Text style={[styles.recoveryBlockedText, { color: theme.colors.error }]}>
	            {detailRecoveryItem.blockedReason}
	          </Text>
	        )}
	        <Text style={[styles.recoverySummaryText, { color: theme.colors.onSurface }]}>
	          This device: {detailRecoveryItem.localSummary}
	        </Text>
	        {detailRecoveryItem.serverSummary && (
	          <Text style={[styles.recoveryServerText, { color: theme.colors.onSurfaceVariant }]}>
	            Online: {detailRecoveryItem.serverSummary}
	          </Text>
	        )}
	        {detailRecoveryItem.detailRows?.map(row => (
	          <View key={`${row.label}-${row.value}`} style={styles.recoveryDetailRow}>
	            <Text style={[styles.recoveryDetailLabel, { color: theme.colors.onSurfaceVariant }]}>
	              {row.label}
	            </Text>
	            <Text style={[styles.recoveryDetailValue, { color: theme.colors.onSurface }]}>
	              {row.value}
	            </Text>
	          </View>
	        ))}
	        {detailRecoveryItem.photoPreviews && detailRecoveryItem.photoPreviews.length > 0 && (
	          <View style={styles.recoveryPhotoGrid}>
	            {detailRecoveryItem.photoPreviews.map(photo => (
	              <View key={String(photo.id ?? photo.localPath ?? photo.fileName)} style={styles.recoveryPhotoTile}>
	                {photo.localPath ? (
	                  <Image
	                    source={{ uri: photo.localPath }}
	                    style={styles.recoveryPhotoImage}
	                    resizeMode="cover"
	                  />
	                ) : null}
	                <Text
	                  numberOfLines={2}
	                  style={[styles.recoveryPhotoCaption, { color: theme.colors.onSurfaceVariant }]}
	                >
	                  {photo.fileName || photo.status || 'Photo'}
	                </Text>
	              </View>
	            ))}
	          </View>
	        )}
	      </View>
	    );
	  };

	  const renderRecoveryDetails = () => {
    if (Platform.OS === 'web' || !recoveryAvailable) {
      return null;
    }

    return (
      <Card
        style={styles.recoveryCard}
        testID="sync-indicator-recovery-details-card"
      >
        <Card.Content>
	          <View style={styles.recoveryHeaderRow}>
	            <View style={styles.recoveryHeaderText}>
              <Text style={[styles.sectionTitle, { color: theme.colors.onSurface }]}>
                Choose Saved Changes
              </Text>
            </View>
	            <IconButton
              icon="refresh"
              size={20}
              onPress={offlineSync.refreshRecoveryWorkItems}
              disabled={recoveryActionLoading}
              testID="sync-indicator-recovery-refresh-button"
	            />
	          </View>
	          {!detailRecoveryItem && recoveryWorkItems.length > 0 && (
	            <View style={styles.recoverySelectAllRow}>
	              <Button
	                mode="text"
	                compact
	                onPress={toggleSelectAllRecoveryItems}
	                disabled={recoveryActionLoading || actionableRecoveryItems.length === 0}
	                testID="sync-indicator-recovery-select-all-button"
	              >
	                {allRecoveryItemsSelected ? 'Clear' : 'Select all'}
	              </Button>
	              <Text style={[styles.recoveryHelpText, { color: theme.colors.onSurfaceVariant }]}>
	                {selectedRecoveryIds.length} selected
	              </Text>
	            </View>
	          )}

	          {detailRecoveryItem ? (
	            renderRecoveryItemDetail()
	          ) : (
            <ScrollView
              style={styles.recoveryListScroll}
              nestedScrollEnabled
              testID="sync-indicator-recovery-list-scroll"
            >
              {recoveryWorkItems.length === 0 ? (
                <View style={styles.recoveryLoadingRow}>
                  <ActivityIndicator size="small" />
                  <Text style={[styles.recoveryHelpText, { color: theme.colors.onSurfaceVariant }]}>
                    Loading saved changes...
                  </Text>
                </View>
              ) : (
                <View style={styles.recoveryList}>
                  {recoveryWorkItems.map(item => {
                  const safeId = recoveryItemTestId(item);
                  const checked = selectedRecoveryIds.includes(item.id);
                  const recommendationText = item.recommendation === 'keep_server'
                    ? 'Online recommended'
                    : item.recommendation === 'recover_local'
                    ? 'Use saved changes'
                    : 'Choose version';

                  return (
                    <TouchableOpacity
                      key={item.id}
                      style={[
                        styles.recoveryItem,
                        {
                          borderColor: checked ? theme.colors.primary : theme.colors.outlineVariant,
                          backgroundColor: checked ? theme.colors.primaryContainer : theme.colors.surface,
                        },
                      ]}
	                      onPress={() => setDetailRecoveryId(item.id)}
	                      testID={`recovery-work-item-${safeId}`}
	                    >
                      <View style={styles.recoveryItemHeader}>
	                        {item.blockedReason ? (
	                          <View pointerEvents="none">
	                            <Checkbox.Android
	                              status="unchecked"
	                              disabled
	                              testID={`recovery-work-item-checkbox-${safeId}`}
	                            />
	                          </View>
	                        ) : (
	                          <Checkbox.Android
	                            status={checked ? 'checked' : 'unchecked'}
	                            onPress={() => toggleRecoverySelection(item.id)}
	                            testID={`recovery-work-item-checkbox-${safeId}`}
	                          />
	                        )}
	                        <View style={styles.recoveryItemTitleColumn}>
	                          <Text style={[styles.recoveryItemTitle, { color: theme.colors.onSurface }]}>
	                            {formatRecoveryTitle(item)}
	                          </Text>
                          <Text
                            testID={`recovery-work-item-meta-${safeId}`}
                            style={[styles.recoveryItemMeta, { color: theme.colors.onSurfaceVariant }]}
                          >
                            {item.blockedReason
                              ? item.blockedReason
                              : `${item.kind === 'response' ? (item.itemName || `Item ${item.itemId ?? item.entityId}`) : item.kind}${item.status ? ` | ${item.status}` : ''}`}
                          </Text>
                        </View>
                        <Chip compact style={styles.recoveryRecommendationChip}>
                          {recommendationText}
                        </Chip>
                      </View>
                      <Text
                        testID={`recovery-work-item-local-${safeId}`}
                        style={[styles.recoverySummaryText, { color: theme.colors.onSurface }]}
                      >
                        This device: {item.localSummary}
                      </Text>
                      {item.serverSummary && (
                        <Text
                          testID={`recovery-work-item-server-${safeId}`}
                          style={[styles.recoveryServerText, { color: theme.colors.onSurfaceVariant }]}
                        >
                          Online: {item.serverSummary}
                        </Text>
                      )}
                    </TouchableOpacity>
                  );
                  })}
                </View>
              )}
            </ScrollView>
          )}

          {recoveryActionMessage && (
            <Text
              testID="sync-indicator-recovery-action-message"
              style={[styles.recoveryActionMessage, { color: theme.colors.onSurfaceVariant }]}
            >
              {recoveryActionMessage}
            </Text>
          )}

	          <View style={styles.recoveryActionRow}>
            <Button
              mode="contained"
              icon="lifebuoy"
              onPress={handleRecoverSelected}
	              disabled={recoveryActionLoading || selectedRecoveryIds.length === 0}
              loading={recoveryActionLoading}
              style={styles.recoveryActionButton}
              testID="sync-indicator-recover-selected-button"
            >
              Keep Changes
            </Button>
            <Button
              mode="outlined"
              icon="cloud-check"
              onPress={handleKeepServerSelected}
	              disabled={recoveryActionLoading || selectedRecoveryIds.length === 0}
              style={styles.recoveryActionButton}
              testID="sync-indicator-keep-server-selected-button"
            >
              Keep Online
            </Button>
          </View>
        </Card.Content>
      </Card>
    );
  };

  const renderExpandedView = () => (
    <Portal>
      <Modal
        visible={visible}
        onDismiss={onDismiss}
        contentContainerStyle={[styles.modalContainer, { backgroundColor: theme.colors.surface }]}
      >
        <ScrollView style={styles.modalContent} testID="sync-indicator-modal-scroll">
          {/* Header */}
          <View style={styles.modalHeader}>
            <View style={styles.headerLeft}>
              <Animated.View
                style={[
                  styles.modalIconContainer,
                  {
                    transform: [{
                      rotate: spinValue.interpolate({
                        inputRange: [0, 1],
                        outputRange: ['0deg', '360deg'],
                      })
                    }]
                  }
                ]}
              >
                <IconButton
                  icon={getStatusIcon()}
                  iconColor={getStatusColor()}
                  size={32}
                />
              </Animated.View>
              <View>
                <Text style={[styles.modalTitle, { color: theme.colors.onSurface }]}>
                  Sync Status
                </Text>
                <Text
                  testID="sync-indicator-modal-subtitle"
                  style={[styles.modalSubtitle, { color: theme.colors.onSurfaceVariant }]}
                >
                  {getEnhancedStatusText()}
                </Text>
              </View>
            </View>
            <IconButton icon="close" onPress={onDismiss} />
          </View>

          {/* Current Sync Progress */}
          {syncInProgress && (
            <Card style={styles.progressCard}>
              <Card.Content>
                <Text style={[styles.sectionTitle, { color: theme.colors.onSurface }]}>
                  Current Sync Operation
                </Text>
                <Text style={[styles.currentOperation, { color: theme.colors.onSurfaceVariant }]}>
                  {offlineState.syncProgress.currentOperation || 'Preparing sync...'}
                </Text>
                <ProgressBar
                  progress={getProgressPercentage()}
                  color={theme.colors.primary}
                  style={styles.modalProgressBar}
                />
                <Text style={[styles.progressText, { color: theme.colors.onSurfaceVariant }]}>
                  {offlineState.syncProgress.completedOperations} of {offlineState.syncProgress.totalOperations} operations completed
                </Text>

                {offlineState.syncProgress.errors.length > 0 && (
                  <View style={styles.errorsContainer}>
                    <Text style={[styles.errorsTitle, { color: theme.colors.error }]}>
                      Sync Errors:
                    </Text>
                    {offlineState.syncProgress.errors.map((error, index) => (
                      <Text key={index} style={[styles.errorText, { color: theme.colors.error }]}>
                        • {error}
                      </Text>
                    ))}
                  </View>
                )}
              </Card.Content>
            </Card>
          )}

	          {/* Recovery Details */}
	          {renderRecoveryDetails()}

	          {/* Queue Visualization */}
	          {renderQueueVisualization()}

	          {/* Connection Status */}
	          <Card style={styles.statusCard}>
            <Card.Content>
              <Text style={[styles.sectionTitle, { color: theme.colors.onSurface }]}>
                Connection Status
              </Text>
              <View style={styles.statusRow}>
                <IconButton
                  icon={isOnline ? 'wifi' : 'wifi-off'}
                  iconColor={isOnline ? theme.colors.primary : theme.colors.error}
                  size={24}
                />
                <Text style={[styles.statusLabel, { color: theme.colors.onSurface }]}>
                  {isOnline ? 'Online' : 'Offline'}
                </Text>
              </View>
              {lastSyncTime && (
                <Text style={[styles.lastSyncText, { color: theme.colors.onSurfaceVariant }]}>
                  Last sync: {formatTimestamp(lastSyncTime)}
                </Text>
              )}
            </Card.Content>
          </Card>

          {/* Sync History */}
          {renderSyncHistory()}

          {/* Actions */}
          {manualSyncMessage && (
            <Text
              testID="sync-indicator-manual-sync-message"
              style={[styles.recoveryActionMessage, { color: theme.colors.onSurfaceVariant }]}
            >
              {manualSyncMessage}
            </Text>
          )}
          <View style={styles.actionsContainer}>
            <Button
              mode="contained"
              onPress={handleManualSync}
              disabled={!isOnline || syncInProgress}
              loading={syncInProgress}
              style={styles.syncButton}
              icon="sync"
              testID="sync-indicator-force-sync-button"
            >
              {syncInProgress ? 'Syncing...' : 'Sync Now'}
            </Button>
            <Button
              mode="outlined"
              onPress={onDismiss}
              style={styles.closeButton}
              testID="sync-indicator-close-button"
            >
              Close
            </Button>
          </View>
        </ScrollView>
      </Modal>
    </Portal>
  );

  if (compact) {
    return renderCompactView();
  }

  return renderExpandedView();
};

const styles = StyleSheet.create({
  // Compact view styles
  compactContainer: {
    borderRadius: 8,
    margin: 8,
    elevation: 2,
  },
  compactContent: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  iconContainer: {
    marginRight: 8,
  },
  statusIcon: {
    margin: 0,
  },
  compactText: {
    flex: 1,
  },
  statusText: {
    fontSize: 14,
    fontWeight: '600',
  },
  pendingText: {
    fontSize: 12,
    marginTop: 2,
  },
  warningText: {
    fontSize: 11,
    marginTop: 2,
    fontWeight: '600',
  },
  statsBreakdown: {
    marginTop: 8,
    marginBottom: 8,
  },
  statItem: {
    fontSize: 13,
    marginVertical: 2,
  },
  compactSyncButton: {
    minWidth: 60,
    marginLeft: 8,
  },
  compactSyncButtonText: {
    fontSize: 12,
  },
  progressBar: {
    height: 2,
    marginTop: 4,
  },

  // Modal styles
  modalContainer: {
    marginHorizontal: 20,
    marginVertical: 60,
    borderRadius: 12,
    maxHeight: Dimensions.get('window').height * 0.8,
  },
  modalContent: {
    padding: 16,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  modalIconContainer: {
    marginRight: 12,
  },
  modalTitle: {
    fontSize: 24,
    fontWeight: 'bold',
  },
  modalSubtitle: {
    fontSize: 16,
    marginTop: 4,
  },

  // Card styles
  progressCard: {
    marginBottom: 16,
  },
  statusCard: {
    marginBottom: 16,
  },
  queueCard: {
    marginBottom: 16,
  },
  historyCard: {
    marginBottom: 16,
  },
  recoveryCard: {
    marginBottom: 16,
  },

  // Section styles
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 12,
  },
  currentOperation: {
    fontSize: 14,
    marginBottom: 8,
  },
  modalProgressBar: {
    height: 6,
    borderRadius: 3,
    marginVertical: 8,
  },
  progressText: {
    fontSize: 12,
    textAlign: 'center',
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  statusLabel: {
    fontSize: 16,
    marginLeft: 8,
  },
  lastSyncText: {
    fontSize: 14,
    marginTop: 8,
  },

  // Queue styles
  emptyQueueText: {
    fontSize: 14,
    fontStyle: 'italic',
    textAlign: 'center',
    paddingVertical: 16,
  },
  queueList: {
    maxHeight: 200,
  },
  queueItem: {
    marginBottom: 8,
  },
  queueItemContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  actionTypeChip: {
    marginRight: 8,
  },
  queueItemText: {
    flex: 1,
    fontSize: 14,
  },
  retryCount: {
    fontSize: 12,
    fontWeight: '600',
  },
  moreItemsText: {
    fontSize: 12,
    textAlign: 'center',
    paddingVertical: 8,
    fontStyle: 'italic',
  },

  // Recovery details styles
  recoveryHeaderRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  recoveryHeaderText: {
    flex: 1,
    paddingRight: 8,
  },
	  recoveryHelpText: {
	    fontSize: 13,
	    lineHeight: 18,
	  },
	  recoverySelectAllRow: {
	    flexDirection: 'row',
	    alignItems: 'center',
	    justifyContent: 'space-between',
	    marginBottom: 8,
	  },
	  recoveryBackButton: {
	    alignSelf: 'flex-start',
	    marginLeft: -8,
	  },
	  recoveryDetailTitle: {
	    fontSize: 17,
	    marginTop: 4,
	  },
	  recoveryBlockedText: {
	    fontSize: 13,
	    lineHeight: 18,
	    marginTop: 8,
	    fontWeight: '600',
	  },
	  recoveryDetailRow: {
	    marginTop: 10,
	  },
	  recoveryDetailLabel: {
	    fontSize: 12,
	    fontWeight: '600',
	  },
	  recoveryDetailValue: {
	    fontSize: 14,
	    marginTop: 2,
	  },
	  recoveryPhotoGrid: {
	    flexDirection: 'row',
	    flexWrap: 'wrap',
	    gap: 8,
	    marginTop: 12,
	  },
	  recoveryPhotoTile: {
	    width: 92,
	  },
	  recoveryPhotoImage: {
	    width: 92,
	    height: 92,
	    borderRadius: 6,
	    backgroundColor: '#eee',
	  },
	  recoveryPhotoCaption: {
	    fontSize: 11,
	    marginTop: 4,
	  },
	  recoveryLoadingRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 12,
  },
  recoveryListScroll: {
    maxHeight: 300,
  },
  recoveryList: {
    gap: 10,
    paddingBottom: 2,
  },
  recoveryItem: {
    borderWidth: 1,
    borderRadius: 8,
    padding: 10,
  },
  recoveryItemHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  recoveryItemTitleColumn: {
    flex: 1,
    minWidth: 0,
  },
  recoveryItemTitle: {
    fontSize: 15,
    fontWeight: '600',
  },
  recoveryItemMeta: {
    fontSize: 12,
    marginTop: 2,
  },
  recoveryRecommendationChip: {
    marginLeft: 8,
    maxWidth: 128,
  },
  recoverySummaryText: {
    fontSize: 13,
    lineHeight: 18,
    marginTop: 8,
  },
  recoveryServerText: {
    fontSize: 12,
    lineHeight: 17,
    marginTop: 4,
  },
  recoveryActionMessage: {
    fontSize: 13,
    marginTop: 10,
  },
  recoveryActionRow: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 12,
  },
  recoveryActionButton: {
    flex: 1,
  },

  // History styles
  emptyHistoryText: {
    fontSize: 14,
    fontStyle: 'italic',
    textAlign: 'center',
    paddingVertical: 16,
  },
  historyList: {
    maxHeight: 300,
  },
  historyItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 8,
  },
  historyItemContent: {
    flex: 1,
    marginLeft: 8,
  },
  historyItemText: {
    fontSize: 14,
    fontWeight: '500',
  },
  historyTimestamp: {
    fontSize: 12,
    marginTop: 2,
  },
  historyDetails: {
    fontSize: 12,
    marginTop: 4,
    fontStyle: 'italic',
  },

  // Error styles
  errorsContainer: {
    marginTop: 12,
    padding: 8,
    borderRadius: 4,
    backgroundColor: 'rgba(255, 0, 0, 0.1)',
  },
  errorsTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 4,
  },
  errorText: {
    fontSize: 12,
    marginBottom: 2,
  },

  // Actions styles
  actionsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 16,
    gap: 12,
  },
  syncButton: {
    flex: 1,
  },
  closeButton: {
    flex: 1,
  },
});

export default SyncIndicator;
